/**
 * The outreach vocabulary — channels, message statuses and consent bases — and the
 * words for each, plus the one client-driven action (approve). The outreach analogue
 * of `web/src/recruiting/offer.ts`, and it exists for the same reason: the compliance
 * facts belong in one table a reader can audit, not scattered across the screen that
 * renders them.
 *
 * ── The compliance reality this table encodes ────────────────────────────────
 *
 * `openapi.yaml`'s own words: "High-risk channels (everything but `email`) can NEVER
 * have `requires_approval` set to `false` — the server forces it back to `true`
 * regardless of what the body asks for, because their adapters are a later sourcing
 * task and this task never sends through one." `HIGH_RISK_OUTREACH_CHANNELS` is that
 * set, transcribed so the screen's warning and the server's CHECK name the same five
 * channels. This is UI honesty, not enforcement — the server's CHECK is what actually
 * stops a request, exactly as `nav/menu.ts`'s own banner says about the menu.
 *
 * ── Why there is no status machine table here ────────────────────────────────
 *
 * Unlike an offer or an application, a message has exactly **one** client-driven
 * transition: `pending_approval → approved`, via `POST …/approve`. Every other move
 * (`approved → sent`, `→ suppressed`, `→ failed`) is decided by the worker's five
 * gates when `dispatch_outreach` next runs, not by a screen action — so there is no
 * second table of "actions per status" the way `offer.ts` needs one for three verbs.
 * `canApproveOutreachMessage` is the whole rule.
 *
 * Transcribed rather than imported from `src/modules/outreach/*`, for the reason
 * `offer.ts` and `interview.ts` both give: the SPA bundle is served to browsers, and a
 * `web/ → src/modules/` import would drag a `pg` handle into one.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { OutreachChannel, OutreachConsentBasis, OutreachMessageStatus } from '../api/outreach';

export {
  OUTREACH_CHANNELS,
  OUTREACH_CONSENT_BASES,
  OUTREACH_MESSAGE_STATUSES,
} from '../api/outreach';
export type { OutreachChannel, OutreachConsentBasis, OutreachMessageStatus } from '../api/outreach';

/** Every channel but `email` — the CHECK's own set, transcribed (see module docblock). */
export const HIGH_RISK_OUTREACH_CHANNELS: readonly OutreachChannel[] = [
  'linkedin_dm',
  'linkedin_comment',
  'facebook_dm',
  'facebook_comment',
  'web_scrape',
];

export function isHighRiskOutreachChannel(channel: OutreachChannel): boolean {
  return channel !== 'email';
}

/** The name of a channel, for the policy table and the message create/list forms. */
export const OUTREACH_CHANNEL_LABEL: Readonly<Record<OutreachChannel, MessageKey>> = {
  email: 'outreach.channel.email',
  linkedin_dm: 'outreach.channel.linkedin_dm',
  linkedin_comment: 'outreach.channel.linkedin_comment',
  facebook_dm: 'outreach.channel.facebook_dm',
  facebook_comment: 'outreach.channel.facebook_comment',
  web_scrape: 'outreach.channel.web_scrape',
};

/** The name of a message status, for the badge. */
export const OUTREACH_MESSAGE_STATUS_LABEL: Readonly<Record<OutreachMessageStatus, MessageKey>> = {
  draft: 'outreach.messages.status.draft',
  pending_approval: 'outreach.messages.status.pending_approval',
  approved: 'outreach.messages.status.approved',
  sent: 'outreach.messages.status.sent',
  suppressed: 'outreach.messages.status.suppressed',
  failed: 'outreach.messages.status.failed',
  cancelled: 'outreach.messages.status.cancelled',
};

/**
 * The badge severity a message status is drawn at. `draft`/`pending_approval`/
 * `approved` are in-flight (`info`); `sent` is the good terminal (`low`);
 * `suppressed` is a compliance stop rather than an alarm (`medium`, and its own icon
 * — `ban` — carries the rest of the meaning); `failed` is a transport problem worth
 * noticing (`high`); `cancelled` is a quiet terminal (`medium`). Icon and text carry
 * the meaning; colour is the third channel (`DESIGN.md` §4).
 */
export const OUTREACH_MESSAGE_STATUS_BADGE: Readonly<Record<OutreachMessageStatus, Severity>> = {
  draft: 'info',
  pending_approval: 'info',
  approved: 'info',
  sent: 'low',
  suppressed: 'medium',
  failed: 'high',
  cancelled: 'medium',
};

/** `true` for exactly the statuses a `dispatch_outreach` run (or nothing) can still move. */
export function isTerminalOutreachMessageStatus(status: OutreachMessageStatus): boolean {
  return (
    status === 'sent' || status === 'suppressed' || status === 'failed' || status === 'cancelled'
  );
}

/**
 * The one client-driven action: `pending_approval → approved`. Every other status —
 * `draft` (no submit-for-approval endpoint exists), `approved` and the four terminals
 * — has no button; the worker decides what happens to it next.
 */
export function canApproveOutreachMessage(status: OutreachMessageStatus): boolean {
  return status === 'pending_approval';
}

/** The name of a consent basis, for the record-consent form. */
export const OUTREACH_CONSENT_BASIS_LABEL: Readonly<Record<OutreachConsentBasis, MessageKey>> = {
  candidate_initiated: 'outreach.consent.basis.candidate_initiated',
  explicit_optin: 'outreach.consent.basis.explicit_optin',
  legitimate_interest: 'outreach.consent.basis.legitimate_interest',
};
